/**
 * InputGroup utilities: inline addon discovery, padding injection, block error extraction.
 */
import React from "react";
import type { InputGroupAddonAlign, InputGroupAddonProps, InputGroupInputProps } from "./InputGroup.types";

/** Flags for inline addon presence; drives padding injection. */
export interface InlineAddonFlags {
  /** True if any addon has align=inline-start. */
  hasInlineStartAddon: boolean;
  /** True if any addon has align=inline-end. */
  hasInlineEndAddon: boolean;
}

/**
 * Scans children for InputGroupAddon; returns which inline addons exist.
 * @param children - InputGroup children
 * @param AddonComponent - InputGroupAddon reference for type check
 */
export function discoverInlineAddons(
  children: React.ReactNode,
  AddonComponent: React.ComponentType<InputGroupAddonProps>
): InlineAddonFlags {
  const flags: InlineAddonFlags = { hasInlineStartAddon: false, hasInlineEndAddon: false };
  React.Children.forEach(children, (rawChild) => {
    const child = rawChild as React.ReactElement<InputGroupAddonProps>;
    if (!React.isValidElement(child) || child.type !== AddonComponent) return;
    const align: InputGroupAddonAlign = child.props.align ?? "inline-end";
    if (align === "inline-start") flags.hasInlineStartAddon = true;
    if (align === "inline-end") flags.hasInlineEndAddon = true;
  });
  return flags;
}

/** Result: enhanced children with inferred padding; blockError when block + error. */
export interface InjectPaddingResult {
  /** Children with padding injected into InputGroupInput. */
  enhancedChildren: React.ReactNode[];
  /** Error string when block + InputGroupInput has error. */
  blockError?: string;
}

/**
 * Clones InputGroupInput with padding; in block mode, strips error → blockError.
 * @param children - InputGroup children
 * @param flags - From discoverInlineAddons
 * @param InputGroupInputRef - InputGroupInput reference for type check
 * @param orientation - inline | block
 */
export function injectPaddingIntoInputs(
  children: React.ReactNode,
  flags: InlineAddonFlags,
  InputGroupInputRef: React.ComponentType<InputGroupInputProps>,
  orientation: "inline" | "block"
): InjectPaddingResult {
  let blockError: string | undefined;
  const enhancedChildren = React.Children.map(children, (rawChild) => {
    const child = rawChild as React.ReactElement<InputGroupInputProps>;
    if (!React.isValidElement(child) || child.type !== InputGroupInputRef) return rawChild;
    const props = child.props as InputGroupInputProps;
    const isBlock = orientation === "block";
    const hasError = Boolean(props.error);

    if (isBlock && hasError) {
      blockError = props.error;
      const { error: _omit, ...rest } = props;
      return React.cloneElement(child as React.ReactElement<InputGroupInputProps>, {
        ...rest,
        "aria-invalid": true,
        startAddonPadding: props.startAddonPadding ?? (flags.hasInlineStartAddon ? "icon" : "none"),
        endAddonPadding: props.endAddonPadding ?? (flags.hasInlineEndAddon ? "icon" : "none"),
      });
    }

    return React.cloneElement(child as React.ReactElement<InputGroupInputProps>, {
      ...props,
      startAddonPadding: props.startAddonPadding ?? (flags.hasInlineStartAddon ? "icon" : "none"),
      endAddonPadding: props.endAddonPadding ?? (flags.hasInlineEndAddon ? "icon" : "none"),
    });
  }) as React.ReactNode[];

  return { enhancedChildren, blockError };
}
